import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    // Load cart from localStorage on initialization
    const savedCart = localStorage.getItem("baypointCart");
    return savedCart ? JSON.parse(savedCart) : [];
  });

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("baypointCart", JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (container) => {
    setCartItems((prevItems) => {
      // Check if item already exists in cart
      const existingItem = prevItems.find((item) => item.id === container.id);

      if (existingItem) {
        // Increase quantity if item already exists
        return prevItems.map((item) =>
          item.id === container.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      } else {
        // Add new item with quantity 1
        return [...prevItems, { ...container, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (containerId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== containerId),
    );
  };

  const updateQuantity = (containerId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(containerId);
      return;
    }

    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === containerId ? { ...item, quantity: newQuantity } : item,
      ),
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartSubtotal = () => {
    // For now, return 0 as we don't have prices on containers
    // You can add price field to containers later
    return 0;
  };

  const isInCart = (containerId) => {
    return cartItems.some((item) => item.id === containerId);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartSubtotal,
    isInCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
